import "./AccountDetailsCard.scss";
import { ButtonType, CDButton } from "@cdatacloud/component-library";
import { useMutation } from "@tanstack/react-query";
import { Form, Formik, FormikHelpers } from "formik";
import { Card, CardBody, Col, FormGroup } from "reactstrap";
import { CDataFormTextField } from "src/components/form/CDataFormTextField";
import { IAccount } from "src/models";
import { IUpdateOrganizationRequest } from "src/models/Accounts/IUpdateOrganizationRequest";
import { accountDetailsValidationSchema } from "src/pages/settings/AccountTab/DetailsCard/accountDetailsValidationSchema";
import { updateOrganization } from "src/pages/settings/api/updateOrganization";
import { addAccountInfo } from "src/redux/actions";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { ToastrSuccess } from "src/services/toastrService";
import { countryList } from "src/utility/Countries";

export function AccountDetailsCard() {
  const account = useAppSelector((s) => s.accountInfo);
  const dispatch = useAppDispatch();

  const { mutateAsync: mutateUpdateOrganizationAsync } = useMutation({
    mutationFn: updateOrganization,
    mutationKey: ["/account/updateOrganization"],
    meta: {
      errorMessage: "Failed to update the organization for the account",
    },
    onSuccess: () => {
      ToastrSuccess(
        "Account Details successfully saved",
        "You have successfully updated your account details.",
      );
    },
  });

  async function onSubmit(values: IAccount, actions: FormikHelpers<IAccount>) {
    const payload: IUpdateOrganizationRequest = {
      organization: values.organization!,
      // This field is always true for non-OEM accounts.
      showDataModelOnEditConnection: true,
    };
    await mutateUpdateOrganizationAsync(payload);

    dispatch(addAccountInfo(account));

    actions.resetForm({
      values,
    });
  }

  return (
    <Formik
      initialValues={account}
      validationSchema={accountDetailsValidationSchema}
      validateOnMount
      onSubmit={(values, actions) => onSubmit(values, actions)}
    >
      {({ handleSubmit, isValid, dirty, isSubmitting }) => {
        return (
          <Form className="accountDetailsForm">
            <Card>
              <CardBody className="accountDetailsCardBody">
                <div className="detailsHeader">
                  <h4 className="mb-4 detailsHeader-text">Details</h4>
                  <div>
                    <CDButton
                      buttonType={ButtonType.Primary}
                      onClick={() => handleSubmit()}
                      className="card-actions float-end"
                      disabled={!isValid || !dirty || isSubmitting}
                      data-testid="oem-details-card-save-changes-button"
                    >
                      <i className="fa fa-save small-icon align-middle no-pointer-event" />
                      Save Changes
                    </CDButton>
                  </div>
                </div>
                <div className="detailsRow">
                  <Col>
                    <FormGroup>
                      <label
                        htmlFor="organization-input"
                        className="fw-bold mb-2"
                      >
                        Organization Name
                      </label>
                      <CDataFormTextField
                        id="organization-input"
                        name="organization"
                        className="organization-name"
                      />
                    </FormGroup>
                  </Col>
                  <Col>
                    <label className="fw-bold mb-2">Country</label>
                    <div className="countryName">
                      {account.country
                        ? countryList.find(
                            (t) => t.threeLetterCode === account.country,
                          )?.name ?? ""
                        : ""}
                    </div>
                  </Col>
                </div>
              </CardBody>
            </Card>
          </Form>
        );
      }}
    </Formik>
  );
}
